import { X, Receipt, CheckCircle2 } from "lucide-react";
import { ymd } from "./utils";

export function EndOfDayModal({ date, items, clients, onClose, onPickAppt }) {
  const dayItems = items
    .filter((a) => ymd(new Date(a.start)) === date)
    .sort((a, b) => new Date(a.start) - new Date(b.start));
  const pending = dayItems.filter((a) => a.status !== "completed");
  const done = dayItems.filter((a) => a.status === "completed");
  const clientName = (id) => clients.find((c) => c.id === id)?.name || "Cliente";
  const hm = (iso) => {
    const d = new Date(iso);
    return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
  };
  const label = new Date(date + "T00:00:00Z").toLocaleDateString("it-IT", {
    weekday: "long", day: "numeric", month: "long", timeZone: "UTC",
  });

  return (
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
         onClick={onClose} data-testid="eod-modal">
      <div className="sp-card w-full max-w-2xl p-6 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display text-2xl font-black inline-flex items-center gap-2">
              <Receipt className="h-6 w-6 text-[color:var(--sp-brand)]" /> Chiusura giornata
            </h3>
            <p className="text-sm text-[color:var(--sp-text-muted)] mt-1 capitalize">{label}</p>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded flex items-center justify-center hover:bg-[color:var(--sp-subtle)]" data-testid="eod-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="rounded-lg border border-[color:var(--sp-border)] p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)]">Totale appuntamenti</div>
            <div className="text-2xl font-black" data-testid="eod-total">{dayItems.length}</div>
          </div>
          <div className="rounded-lg border border-[color:var(--sp-border)] p-3 bg-emerald-50/40">
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Chiusi</div>
            <div className="text-2xl font-black text-emerald-700" data-testid="eod-done">{done.length}</div>
          </div>
          <div className="rounded-lg border border-[color:var(--sp-border)] p-3 bg-amber-50/40">
            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Da chiudere</div>
            <div className="text-2xl font-black text-amber-700" data-testid="eod-pending">{pending.length}</div>
          </div>
        </div>

        {dayItems.length === 0 ? (
          <div className="py-8 text-center text-sm text-[color:var(--sp-text-muted)]">
            Nessun appuntamento programmato per oggi.
          </div>
        ) : pending.length === 0 ? (
          <div className="py-8 text-center">
            <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500 mb-2" />
            <div className="font-bold">Tutti gli appuntamenti sono stati chiusi</div>
            <div className="text-xs text-[color:var(--sp-text-muted)] mt-1">Buon lavoro!</div>
          </div>
        ) : (
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-[color:var(--sp-text-muted)] mb-2">
              Da chiudere ({pending.length})
            </h4>
            <div className="space-y-2 max-h-[420px] overflow-y-auto">
              {pending.map((a) => (
                <div key={a.id} data-testid={`eod-item-${a.id}`}
                     className="flex items-center gap-3 rounded-lg border border-[color:var(--sp-border)] p-3 hover:border-[color:var(--sp-brand)]/60 transition-colors bg-white">
                  <div className="text-xs font-black text-[color:var(--sp-text-muted)] w-24 flex-shrink-0">
                    {hm(a.start)}–{hm(a.end)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{clientName(a.client_id)}</div>
                    {a.notes && (
                      <div className="text-xs text-[color:var(--sp-text-muted)] truncate">{a.notes}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => onPickAppt(a)}
                    data-testid={`eod-close-appt-${a.id}`}
                    className="sp-btn-primary inline-flex items-center gap-1.5 !py-1.5 !px-3 text-xs whitespace-nowrap">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Chiudi e incassa
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {done.length > 0 && (
          <div className="mt-5 pt-4 border-t border-[color:var(--sp-border)]">
            <h4 className="text-xs font-bold uppercase tracking-widest text-[color:var(--sp-text-muted)] mb-2">
              Già chiusi ({done.length})
            </h4>
            <div className="space-y-1">
              {done.map((a) => (
                <div key={a.id} className="flex items-center gap-3 text-xs text-[color:var(--sp-text-muted)] px-3 py-1.5">
                  <CheckCircle2 className="h-3 w-3 text-emerald-500 flex-shrink-0" />
                  <span className="w-24 flex-shrink-0 font-mono">{hm(a.start)}–{hm(a.end)}</span>
                  <span className="line-through truncate">{clientName(a.client_id)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
